import React from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { FileRejection, useDropzone } from 'react-dropzone';

interface PdfToPptUploadProps {
  onFileSelected?: (file: File | null) => void;
  title?: string;
  helperText?: string;
}

export default function PdfToPptUpload({
  onFileSelected,
  title = 'Upload PDF',
  helperText = 'Drag and drop a PDF file here, or click to browse.',
}: PdfToPptUploadProps) {
  const theme = useTheme();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const handleAcceptedFile = React.useCallback((file: File) => {
    setSelectedFile(file);
    setError(null);
    onFileSelected?.(file);
  }, [onFileSelected]);

  const handleRejectedFiles = React.useCallback((rejections: FileRejection[]) => {
    const firstError = rejections[0]?.errors[0];
    const message = firstError?.code === 'file-invalid-type'
      ? 'Only PDF files are allowed.'
      : firstError?.message || 'Unable to upload file.';
    setSelectedFile(null);
    setError(message);
    onFileSelected?.(null);
  }, [onFileSelected]);

  const handleRemoveFile = React.useCallback(() => {
    setSelectedFile(null);
    setError(null);
    onFileSelected?.(null);
  }, [onFileSelected]);

  const onDrop = React.useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      handleRejectedFiles(fileRejections);
      return;
    }

    const file = acceptedFiles[0];
    if (!file) {
      setSelectedFile(null);
      onFileSelected?.(null);
      return;
    }

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setSelectedFile(null);
      setError('Only PDF files are allowed.');
      onFileSelected?.(null);
      return;
    }

    handleAcceptedFile(file);
  }, [handleAcceptedFile, handleRejectedFiles, onFileSelected]);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    noClick: true,
    onDrop,
  });

  let borderColor = theme.palette.grey[400];
  if (isDragAccept) borderColor = theme.palette.primary.main;
  if (isDragReject) borderColor = theme.palette.error.main;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        {...getRootProps()}
        variant="outlined"
        sx={{
          p: 4,
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor,
          borderRadius: 3,
          backgroundColor: isDragActive ? theme.palette.action.hover : theme.palette.background.default,
          cursor: 'pointer',
          textAlign: 'center',
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
        <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isDragActive ? 'Drop the PDF file here.' : helperText}
        </Typography>
        <Button variant="contained" onClick={open} startIcon={<PictureAsPdfIcon />}>
          Choose PDF
        </Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
          Only `.pdf` files are accepted.
        </Typography>
      </Paper>

      {selectedFile && (
        <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Selected file
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
            <Button
              color="error"
              variant="text"
              startIcon={<DeleteIcon />}
              onClick={handleRemoveFile}
            >
              Remove
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
